import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDay } from './amlDates';
import './Aml.css';
import AmlNav from './AmlNav';
import useAmlFramework from './useAmlFramework';
import { canManageAml } from './amlPermissions';
import {
  listCustomerSegments, createCustomerSegment, updateCustomerSegment, deleteCustomerSegment,
  listSegmentSamples, addSegmentSample, uploadSampleEvidence, downloadSampleEvidence, saveBlob, importCoreBankingFile,
} from './amlApi';

const riskClass = (r) => (r === 'HIGH' ? 'aml-pill-high' : r === 'MEDIUM' ? 'aml-pill-medium' : 'aml-pill-low');

export default function AmlCustomerSegments() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManage = canManageAml();
  const { status, frameworks, frameworkId, setFrameworkId } = useAmlFramework();
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState('ALL');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', riskRating: 'MEDIUM', reviewCycleMonths: 12 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [samples, setSamples] = useState([]);
  const [sampleForm, setSampleForm] = useState({ sampleRef: '', reviewedBy: '', outcome: 'PENDING', notes: '' });
  const [uploadingSampleId, setUploadingSampleId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [rowError, setRowError] = useState('');

  const load = () => {
    if (!frameworkId) return;
    setLoading(true);
    listCustomerSegments(frameworkId).then((rows) => { setSegments(rows); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, [frameworkId]);

  const toggleExpand = (s) => {
    if (expandedId === s.id) { setExpandedId(null); return; }
    setExpandedId(s.id);
    setEditForm({ riskRating: s.riskRating, reviewCycleMonths: s.reviewCycleMonths });
    setRowError('');
    setSamples([]);
    listSegmentSamples(frameworkId, s.id).then(setSamples).catch(() => {});
  };

  const saveEdit = async (id) => {
    setEditSaving(true);
    setRowError('');
    try {
      await updateCustomerSegment(frameworkId, id, editForm);
      load();
    } catch (e) {
      setRowError(e?.response?.data?.message || t('Aml.saveFailed', 'Could not save the segment.'));
    } finally {
      setEditSaving(false);
    }
  };

  const removeSegment = async (s) => {
    if (!window.confirm(t('Aml.deleteSegmentConfirm', 'Delete segment "{{name}}"? This cannot be undone.', { name: s.name }))) return;
    setRowError('');
    try {
      await deleteCustomerSegment(frameworkId, s.id);
      setExpandedId(null);
      load();
    } catch (e) {
      setRowError(e?.response?.data?.message || t('Aml.deleteFailed', 'Could not delete.'));
    }
  };

  const fetchSampleEvidence = async (segmentId, sm) => {
    try {
      saveBlob(await downloadSampleEvidence(frameworkId, segmentId, sm.id), sm.evidenceFileName);
    } catch (e) {
      setRowError(t('Aml.downloadFailed', 'Could not download the file.'));
    }
  };

  const attachSampleEvidence = async (segmentId, sampleId, file) => {
    if (!file) return;
    setUploadingSampleId(sampleId);
    setRowError('');
    try {
      await uploadSampleEvidence(frameworkId, segmentId, sampleId, file);
      listSegmentSamples(frameworkId, segmentId).then(setSamples).catch(() => {});
    } catch (e) {
      setRowError(e?.response?.data?.message || t('Aml.evidenceUploadFailed', 'Could not upload the evidence file.'));
    } finally {
      setUploadingSampleId(null);
    }
  };

  const submitSample = async (segmentId) => {
    if (!sampleForm.sampleRef.trim()) return;
    setRowError('');
    try {
      await addSegmentSample(frameworkId, segmentId, sampleForm);
      setSampleForm({ sampleRef: '', reviewedBy: '', outcome: 'PENDING', notes: '' });
      listSegmentSamples(frameworkId, segmentId).then(setSamples).catch(() => {});
    } catch (e) {
      setRowError(e?.response?.data?.message || t('Aml.sampleFailed', 'Could not log the sample.'));
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await importCoreBankingFile(frameworkId, file);
      setImportResult({ ok: true, ...result });
      load();
    } catch (e) {
      setImportResult({ ok: false, message: e?.response?.data?.message || t('Aml.importFailed', 'Import failed.') });
    } finally {
      setImporting(false);
    }
  };

  const visible = segments.filter((s) =>
    (riskFilter === 'ALL' || s.riskRating === riskFilter) &&
    (!search.trim() || s.name.toLowerCase().includes(search.trim().toLowerCase())));

  const totals = segments.reduce((acc, s) => ({
    members: acc.members + (s.memberCount || 0),
    overdue: acc.overdue + (s.overdueCount || 0),
    highMembers: acc.highMembers + (s.riskRating === 'HIGH' ? (s.memberCount || 0) : 0),
  }), { members: 0, overdue: 0, highMembers: 0 });
  const overduePct = totals.members > 0 ? ((totals.overdue / totals.members) * 100).toFixed(1) : '0.0';

  const submitAdd = async () => {
    if (!form.name.trim()) { setError(t('Aml.nameRequired', 'A name is required.')); return; }
    setSaving(true);
    setError('');
    try {
      await createCustomerSegment(frameworkId, form);
      setShowAdd(false);
      setForm({ name: '', riskRating: 'MEDIUM', reviewCycleMonths: 12 });
      load();
    } catch (e) {
      setError(e?.response?.data?.message || t('Aml.saveFailed', 'Could not save the segment.'));
    } finally {
      setSaving(false);
    }
  };

  if (status === 'loading' || loading) return <div className="aml-page"><div className="aml-spinner" /></div>;

  if (status === 'error') {
    return (
      <div className="aml-page">
        <div className="aml-empty">
          {t('Aml.loadError', 'Could not load the AML Program. Your session may have expired — try reloading.')}
        </div>
      </div>
    );
  }

  if (status === 'not-enrolled') {
    return (
      <div className="aml-page">
        <div className="aml-empty">
          {t('Aml.notEnrolled', 'Your organization has not enrolled in an AML framework yet. Enroll in SEPS-IGT-2025-0113 or another AML framework from the Frameworks page to activate the AML Program.')}
          <div style={{ marginTop: '0.75rem' }}>
            <button className="aml-btn aml-btn-primary" onClick={() => navigate('/dashboard/compliance/frameworks')}>
              {t('Aml.goToFrameworks', 'Go to Frameworks')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aml-page">
      <div className="aml-header">
        <div>
          <h1 className="aml-title">{t('Aml.segmentsTitle', 'Customer Segments')}</h1>
          <p className="aml-subtitle">
            {t('Aml.segmentsSubtitle', 'Due-diligence review cycles per risk segment. Counts come from your core-banking connector; CATAAM never stores customer records.')}
          </p>
        </div>
      </div>

      <div className="aml-stat-bar">
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.statSegmentsCount', 'SEGMENTS')}</div><div className="aml-stat-val">{segments.length}</div></div>
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.statHighRiskMembers', 'HIGH-RISK MEMBERS')}</div><div className="aml-stat-val">{totals.highMembers.toLocaleString()}</div></div>
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.statReviewsOverdue', 'REVIEWS OVERDUE')}</div><div className={`aml-stat-val ${totals.overdue > 0 ? 'aml-val-bad' : ''}`}>{overduePct}%</div></div>
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.statTotalOverdue', 'TOTAL OVERDUE')}</div><div className="aml-stat-val">{totals.overdue.toLocaleString()}</div></div>
      </div>

      <AmlNav frameworks={frameworks} frameworkId={frameworkId} onFrameworkChange={setFrameworkId} />

      <div className="aml-toolbar">
        <div className="aml-toolbar-group">
          <label htmlFor="aml-seg-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>{t('Aml.searchSegments', 'Search segments')}</label>
          <input id="aml-seg-search" type="search" className="aml-input" style={{ width: 220 }}
            placeholder={t('Aml.searchSegmentsPlaceholder', 'Search segments...')} value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="aml-select" value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)}>
            <option value="ALL">{t('Aml.allRiskLevels', 'All risk levels')}</option>
            <option value="HIGH">{t('Aml.high', 'HIGH')}</option>
            <option value="MEDIUM">{t('Aml.medium', 'MEDIUM')}</option>
            <option value="LOW">{t('Aml.low', 'LOW')}</option>
          </select>
        </div>
        <div className="aml-toolbar-group">
          <button className="aml-btn" onClick={() => navigate(`/dashboard/aml/vendors?frameworkId=${frameworkId}`)}>{t('Aml.dataSource', 'Data source')}</button>
          {canManage && (
            <>
              <label className="aml-btn" style={{ cursor: 'pointer' }}>
                {importing ? t('Aml.importing', 'Importing…') : t('Aml.importFile', 'Import core-banking file (CSV or Excel)')}
                <input type="file" accept=".csv,.xlsx" style={{ display: 'none' }} disabled={importing}
                  onChange={(e) => handleImport(e.target.files[0])} />
              </label>
              <button className="aml-btn aml-btn-primary" onClick={() => setShowAdd((s) => !s)}>{t('Aml.addSegment', '+ Add segment')}</button>
            </>
          )}
        </div>
      </div>

      {importResult && (
        <div className="aml-section" style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
          {importResult.ok ? (
            <span>
              {t('Aml.importRows', '{{count}} rows read.', { count: importResult.rowsRead })}{' '}
              {t('Aml.importSynced', 'Synced: {{segments}}.', { segments: importResult.segmentsSynced.join(', ') || '—' })}{' '}
              {importResult.segmentsNotInFile?.length > 0 && (
                <span>
                  {t('Aml.importNotInFile', 'Not in this file, counts set to 0: {{segments}}.', { segments: importResult.segmentsNotInFile.join(', ') })}{' '}
                </span>
              )}
              {importResult.unmatchedSegments.length > 0 && (
                <span style={{ color: '#b45309' }}>
                  {t('Aml.importUnmatched', 'Unmatched (create these segments first): {{segments}}.', { segments: importResult.unmatchedSegments.join(', ') })}
                </span>
              )}
              {importResult.invalidDateRows > 0 && (
                <span style={{ color: '#b45309' }}>
                  {' '}
                  {t('Aml.importInvalidDates', '{{count}} rows have a review date that could not be read (lines {{lines}}). They are counted as members but not as overdue or due. Use YYYY-MM-DD or DD/MM/YYYY.', {
                    count: importResult.invalidDateRows,
                    lines: (importResult.invalidDateLines || []).join(', ') + (importResult.invalidDateRows > (importResult.invalidDateLines || []).length ? ', …' : ''),
                  })}
                </span>
              )}
            </span>
          ) : (
            <span style={{ color: '#b91c1c' }}>{importResult.message}</span>
          )}
        </div>
      )}

      {showAdd && (
        <div className="aml-section" style={{ padding: '1rem' }}>
          {error && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
            <label>
              <div className="aml-expand-label">{t('Aml.segmentName', 'Name')}</div>
              <input className="aml-input" style={{ width: '100%' }} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.riskRating', 'Risk rating')}</div>
              <select className="aml-select" style={{ width: '100%' }} value={form.riskRating} onChange={(e) => setForm({ ...form, riskRating: e.target.value })}>
                <option value="HIGH">HIGH</option><option value="MEDIUM">MEDIUM</option><option value="LOW">LOW</option>
              </select>
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.reviewCycleMonths', 'Review cycle (months)')}</div>
              <input type="number" min={1} className="aml-input" style={{ width: '100%' }} value={form.reviewCycleMonths}
                onChange={(e) => setForm({ ...form, reviewCycleMonths: Number(e.target.value) })} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.875rem' }}>
            <button className="aml-btn aml-btn-primary" disabled={saving} onClick={submitAdd}>{t('Aml.save', 'Save')}</button>
            <button className="aml-btn" onClick={() => setShowAdd(false)}>{t('Aml.cancel', 'Cancel')}</button>
          </div>
        </div>
      )}

      <div className="aml-section">
        <div className="aml-table-wrap">
          <table className="aml-table">
            <thead>
              <tr>
                <th>{t('Aml.segment', 'SEGMENT')}</th><th>{t('Aml.members', 'MEMBERS')}</th><th>{t('Aml.riskCol', 'RISK')}</th>
                <th>{t('Aml.reviewCycle', 'REVIEW CYCLE')}</th><th>{t('Aml.dueIn30', 'DUE IN 30 DAYS')}</th><th>{t('Aml.overdueCol', 'OVERDUE')}</th><th />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={7} className="aml-empty">{t('Aml.noSegments', 'No segments yet. Connect core banking or add one manually.')}</td></tr>
              )}
              {visible.map((s) => (
                <React.Fragment key={s.id}>
                  <tr>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td>{(s.memberCount || 0).toLocaleString()}</td>
                    <td><span className={`aml-pill ${riskClass(s.riskRating)}`}>{s.riskRating}</span></td>
                    <td style={{ color: '#475569' }}>{s.reviewCycleMonths} {t('Aml.months', 'months')}</td>
                    <td>{s.dueIn30Days || 0}</td>
                    <td style={{ fontWeight: s.overdueCount > 0 ? 700 : 400, color: s.overdueCount > 0 ? '#dc2626' : 'inherit' }}>{s.overdueCount || 0}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="aml-btn" style={{ height: 30, padding: '0 0.625rem', fontSize: '0.8125rem' }}
                        onClick={() => toggleExpand(s)}>
                        {expandedId === s.id ? t('Aml.hide', 'Hide ▴') : t('Aml.view', 'View ▾')}
                      </button>
                    </td>
                  </tr>
                  {expandedId === s.id && editForm && (
                    <tr className="aml-expand-row">
                      <td colSpan={7}>
                        {canManage && (
                          <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                              <label>
                                <div className="aml-expand-label">{t('Aml.riskRating', 'Risk rating')}</div>
                                <select className="aml-select" style={{ width: '100%' }} value={editForm.riskRating}
                                  onChange={(e) => setEditForm({ ...editForm, riskRating: e.target.value })}>
                                  <option value="HIGH">HIGH</option><option value="MEDIUM">MEDIUM</option><option value="LOW">LOW</option>
                                </select>
                              </label>
                              <label>
                                <div className="aml-expand-label">{t('Aml.reviewCycleMonths', 'Review cycle (months)')}</div>
                                <input type="number" min={1} className="aml-input" style={{ width: '100%' }} value={editForm.reviewCycleMonths}
                                  onChange={(e) => setEditForm({ ...editForm, reviewCycleMonths: Number(e.target.value) })} />
                              </label>
                            </div>
                            <button className="aml-btn aml-btn-primary" style={{ marginTop: '0.625rem' }} disabled={editSaving}
                              onClick={() => saveEdit(s.id)}>{t('Aml.save', 'Save')}</button>
                            <button className="aml-btn" style={{ marginTop: '0.625rem', marginLeft: '0.5rem', color: '#b91c1c' }}
                              onClick={() => removeSegment(s)}>{t('Aml.deleteSegment', 'Delete segment')}</button>
                          </>
                        )}
                        {rowError && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginTop: '0.5rem' }}>{rowError}</div>}

                        <div style={{ marginTop: '1.25rem' }}>
                          <div className="aml-expand-label">{t('Aml.qualitySampling', 'QUALITY SAMPLING')}</div>
                          {canManage && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem', marginTop: '0.375rem' }}>
                              <input className="aml-input" placeholder={t('Aml.sampleRef', 'Sample ref (case #)')}
                                value={sampleForm.sampleRef} onChange={(e) => setSampleForm({ ...sampleForm, sampleRef: e.target.value })} />
                              <input className="aml-input" placeholder={t('Aml.reviewedBy', 'Reviewed by')}
                                value={sampleForm.reviewedBy} onChange={(e) => setSampleForm({ ...sampleForm, reviewedBy: e.target.value })} />
                              <select className="aml-select" value={sampleForm.outcome}
                                onChange={(e) => setSampleForm({ ...sampleForm, outcome: e.target.value })}>
                                <option value="PENDING">{t('Aml.outcomePending', 'Pending')}</option>
                                <option value="CLEAN">{t('Aml.outcomeClean', 'Clean')}</option>
                                <option value="ESCALATED">{t('Aml.outcomeEscalated', 'Escalated')}</option>
                              </select>
                              <button className="aml-btn" onClick={() => submitSample(s.id)}>{t('Aml.logSample', 'Log sample')}</button>
                            </div>
                          )}
                          {canManage && (
                            <div className="aml-muted" style={{ fontSize: '0.6875rem', marginTop: '0.375rem' }}>
                              {t('Aml.sampleEvidenceHint', 'Attach the working paper behind each sample. Reference members by internal ID, never by name.')}
                            </div>
                          )}
                          {!canManage && samples.length === 0 && (
                            <div className="aml-muted" style={{ fontSize: '0.8125rem', marginTop: '0.375rem' }}>
                              {t('Aml.noSamples', 'No samples logged yet.')}
                            </div>
                          )}
                          {samples.length > 0 && (
                            <table className="aml-table" style={{ marginTop: '0.625rem' }}>
                              <thead><tr><th>{t('Aml.sampleRefCol', 'REF')}</th><th>{t('Aml.reviewedBy', 'REVIEWED BY')}</th><th>{t('Aml.outcomeCol', 'OUTCOME')}</th><th>{t('Aml.sampledAt', 'DATE')}</th><th>{t('Aml.evidenceCol', 'EVIDENCE')}</th></tr></thead>
                              <tbody>
                                {samples.map((sm) => (
                                  <tr key={sm.id}>
                                    <td>{sm.sampleRef}</td>
                                    <td>{sm.reviewedBy || '—'}</td>
                                    <td><span className="aml-badge aml-pill-muted">{sm.outcome}</span></td>
                                    <td>{formatDay(sm.sampledAt)}</td>
                                    <td>
                                      {sm.evidenceFileName ? (
                                        canManage ? (
                                          <button type="button" className="aml-link-btn" title={formatDay(sm.evidenceUploadedAt)}
                                            onClick={() => fetchSampleEvidence(s.id, sm)}>📎 {sm.evidenceFileName}</button>
                                        ) : (
                                          <span title={formatDay(sm.evidenceUploadedAt)}>📎 {sm.evidenceFileName}</span>
                                        )
                                      ) : canManage ? (
                                        <label className="aml-btn" style={{ height: 28, padding: '0 0.625rem', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                                          {uploadingSampleId === sm.id ? t('Aml.uploading', 'Uploading…') : t('Aml.attachEvidence', 'Attach')}
                                          <input type="file" style={{ display: 'none' }} disabled={uploadingSampleId === sm.id}
                                            onChange={(e) => attachSampleEvidence(s.id, sm.id, e.target.files[0])} />
                                        </label>
                                      ) : (
                                        <span className="aml-pill aml-pill-medium">{t('Aml.noEvidence', 'No evidence')}</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="aml-table-foot">
          <span>{t('Aml.showing', '{{count}} of {{total}}', { count: visible.length, total: segments.length })}</span>
        </div>
      </div>
    </div>
  );
}
